import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useToast } from 'primevue/usetoast';
import { z } from 'zod';
import { useAuthSession } from '~/composables/auth/useAuthSession';
import type { RegisterForm } from '~/types/auth/forms/RegisterForm';
import { INVALID_INVITE_CODE } from '~/types/ErrorCode';
import RegisterFormContent from '~/utils/contents/auth/forms/RegisterForm';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useRegister({
  initialForm,
}: { initialForm?: Partial<RegisterForm> } = {}) {
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const form = ref<RegisterForm>({
    ...getInitialState(),
    ...Object.fromEntries(
      Object.entries(initialForm || {}).filter(([, v]) => v !== undefined),
    ),
  });

  const { signUp } = useAuthSession();
  const toast = useToast();

  const validations = z
    .object({
      username: z
        .string()
        .default('')
        .pipe(
          z
            .string()
            .min(1, {
              message: `${RegisterFormContent.fields.email.label} is required.`,
            })
            .email({
              message: `${RegisterFormContent.fields.email.label} must be a valid email.`,
            }),
        ),
      password: z
        .string()
        .default('')
        .pipe(
          z
            .string()
            .min(8, {
              message: `${RegisterFormContent.fields.password.label} must be at least 8 characters.`,
            })
            .regex(/[A-Z]/, {
              message: `${RegisterFormContent.fields.password.label} must contain at least one uppercase letter.`,
            })
            .regex(/[a-z]/, {
              message: `${RegisterFormContent.fields.password.label} must contain at least one lowercase letter.`,
            })
            .regex(/[0-9]/, {
              message: `${RegisterFormContent.fields.password.label} must contain at least one number.`,
            })
            .regex(/[^A-Za-z0-9]/, {
              message: `${RegisterFormContent.fields.password.label} must contain at least one special character.`,
            }),
        ),
      password_confirmation: z
        .string()
        .default('')
        .pipe(
          z.string().min(1, {
            message: `${RegisterFormContent.fields.password_confirmation.label} is required.`,
          }),
        ),
      invite_code: z.string().optional(),
    })
    .partial()
    .refine((data) => data.password === data.password_confirmation, {
      message: "Passwords don't match. Please try again.",
      path: ['password_confirmation'],
    });
  const resolver = zodResolver(validations);

  const register = async (): Promise<boolean> => {
    if (!validations.safeParse(form.value).success) {
      return false;
    }

    loading.value = true;

    try {
      const { error } = (await signUp({
        username: form.value.username,
        password: form.value.password,
        passwordConfirmation: form.value.password_confirmation,
        inviteCode: form.value.invite_code,
      })) as {
        error?: {
          data?: {
            error_code?: string;
            http_status?: number;
            success?: boolean;
            errors?: Record<string, string[]>;
            message?: string;
          };
        };
      };

      if (error) {
        const apiErrors = error.data?.errors;
        errors.value = apiErrors || {};
        if (!apiErrors) {
          if (error.data?.error_code === INVALID_INVITE_CODE) {
            errors.value.email = [
              error.data?.message || 'Invalid invite code. Please try again.',
            ];
          }
        }

        return false;
      } else {
        reset();
        return true;
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: $parseErrorMessage(error),
        life: 3000,
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const reset = (): void => {
    form.value = getInitialState();
  };

  function getInitialState(): RegisterForm {
    return {
      username: '',
      password: '',
      password_confirmation: '',
    };
  }

  return {
    loading,
    form,
    errors,
    validations,
    resolver,

    register,
    reset,
  };
}
